/**
 * Project settings: the name, who else can see it, and how to leave or delete it.
 *
 * Members are invited by email address. The invite is stored against the address, so it works
 * whether or not that person has an account yet; it is claimed the first time they sign in.
 */

import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { MemberRole } from '../../shared/types.js';
import {
  deleteProject,
  inviteMember,
  removeMember,
  renameProject,
  setShareLinkPreview,
} from '../data/mutations.js';
import { useStore } from '../data/context.js';
import { useMemberships, useOwnRole, useProject } from '../data/selectors.js';
import { useSession } from '../auth/session.js';
import { PlusIcon, TrashIcon } from '../ui/icons.js';
import { Button, Dialog, EmptyState, TextField, ToggleRow } from '../ui/primitives.js';
import { ProjectFrame } from '../ui/Shell.js';

/** Renders the settings screen. */
export function SettingsPage(): ReactNode {
  const { projectId } = useParams();
  const store = useStore();
  const navigate = useNavigate();
  const project = useProject(projectId);
  const memberships = useMemberships(projectId);
  const role = useOwnRole(projectId);
  const { session } = useSession();
  const [name, setName] = useState<string | null>(null);
  const [invitee, setInvitee] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  if (project === undefined) {
    return <ProjectFrame><EmptyState message="This project is not on this device." /></ProjectFrame>;
  }

  const isOwner = role === MemberRole.Owner;
  const ownMembership = memberships.find((membership) => membership.email === session?.email);
  const draftName = name ?? project.name;

  return (
    <ProjectFrame>
      <section className="px-4 pt-6 pb-5">
        <TextField label="Project name" value={draftName} onChange={setName} />
        {draftName.trim() !== project.name && draftName.trim() !== '' ? (
          <div className="mt-3 flex gap-2">
            <Button
              tone="primary"
              onClick={() => { void renameProject(store, project.id, draftName); setName(null); }}
            >
              Save name
            </Button>
            <Button onClick={() => setName(null)}>Cancel</Button>
          </div>
        ) : null}
      </section>

      <section className="border-t border-line px-4 py-5">
        {isOwner ? null : (
          <p className="text-tiny text-ink-3">Only an owner can change who has access.</p>
        )}

        <ul className="border-t border-line">
          {memberships.map((membership) => (
            <li key={membership.id} className="flex items-center justify-between gap-3 border-b border-line py-3">
              <div className="min-w-0">
                <p className="truncate text-row">{membership.email}</p>
                <p className="text-tiny text-ink-3">
                  {membership.role === MemberRole.Owner ? 'Owner' : 'Member'}
                  {membership.userId === null ? ' · invited, has not signed in yet' : ''}
                  {membership.email === session?.email ? ' · you' : ''}
                </p>
              </div>
              {isOwner && membership.email !== session?.email ? (
                <Button tone="danger" onClick={() => void removeMember(store, membership.id)}>
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>

        {isOwner ? (
          <form
            className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              if (!invitee.includes('@')) return;
              void inviteMember(store, project.id, invitee);
              setInvitee('');
            }}
          >
            <div className="flex-1">
              <TextField
                label="Invite by email"
                type="email"
                value={invitee}
                onChange={setInvitee}
                placeholder="colleague@example.com"
                hint="They get access as soon as they sign in with that address."
              />
            </div>
            <Button type="submit" tone="primary" disabled={!invitee.includes('@')}>
              <PlusIcon size={18} />
              Invite
            </Button>
          </form>
        ) : null}
      </section>

      <section className="border-t border-line px-4 py-5">
        <ToggleRow
          label="Show this project in link previews"
          description="When a link to this project is pasted into a chat, show its name and how many people are on the roster."
          checked={project.shareLinkPreview}
          onChange={(value) => void setShareLinkPreview(store, project.id, value)}
        />
        <p className="mt-2 text-tiny text-ink-2">
          {project.shareLinkPreview
            ? `Anyone holding the link sees “${project.name}” and the roster size without signing in, and so does the chat service that draws the preview. Attendance figures and the names on the roster are never included.`
            : 'Links show only the app name and a generic banner. A link to this project looks the same as a link to one that does not exist.'}
        </p>
      </section>

      <section className="border-t border-line px-4 py-5">
        <p className="text-tiny text-ink-2">
          This device keeps a full copy of the project so roll call works without a connection.
          Usage patterns are counted on this device to preload the screen you are likely to open
          next; nothing about them is sent anywhere.
        </p>
      </section>

      <section className="border-t border-line px-4 py-6">
        <div className="flex flex-wrap gap-3">
          {ownMembership === undefined ? null : (
            <Button tone="danger" onClick={() => setConfirmingLeave(true)}>Leave this project</Button>
          )}
          {isOwner ? (
            <Button tone="danger" onClick={() => setConfirmingDelete(true)}>
              <TrashIcon size={18} />
              Delete this project
            </Button>
          ) : null}
        </div>
      </section>

      {confirmingLeave && ownMembership !== undefined ? (
        <Dialog
          title="Leave this project?"
          onClose={() => setConfirmingLeave(false)}
          footer={
            <>
              <Button onClick={() => setConfirmingLeave(false)}>Cancel</Button>
              <Button
                tone="danger"
                onClick={() => {
                  void removeMember(store, ownMembership.id);
                  void navigate('/');
                }}
              >
                Leave
              </Button>
            </>
          }
        >
          <p>You will lose access to {project.name}. An owner can invite you again.</p>
        </Dialog>
      ) : null}

      {confirmingDelete ? (
        <Dialog
          title={`Delete ${project.name}?`}
          onClose={() => setConfirmingDelete(false)}
          footer={
            <>
              <Button onClick={() => setConfirmingDelete(false)}>Cancel</Button>
              <Button
                tone="danger"
                onClick={() => {
                  void deleteProject(store, project.id);
                  void navigate('/');
                }}
              >
                Delete
              </Button>
            </>
          }
        >
          <p>
            The roster, the schedule, and every recorded mark are deleted for everyone with access.
            This cannot be undone from the app.
          </p>
        </Dialog>
      ) : null}
    </ProjectFrame>
  );
}
